/**
 * offlineDB — IndexedDB store for the technician offline workflow.
 *
 * Stores
 *   bundle  : one row ("latest") — the pre-cached working set from
 *             GET /api/tech/offline-bundle (cards, WOs, inspections,
 *             the whole template library).
 *   drafts  : one row per inspection being worked on, keyed by the
 *             inspection id (server id when it has one, otherwise the
 *             locally-minted id). Survives app close + phone reboot.
 *   photos  : captured photo blobs keyed by a local photo id. Referenced
 *             from a draft as "local://<photoId>" until uploaded.
 *   meta    : misc key/value (last sync time, etc).
 *
 * Nothing here deletes a draft or a photo — only `inspectionSync` does,
 * and only after the server has acknowledged the record.
 */
const DB_NAME = "serviceops_tech_offline";
const DB_VERSION = 1;
export const STORES = { bundle: "bundle", drafts: "drafts", photos: "photos", meta: "meta" };

let _db = null;

function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexedDB")); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.bundle)) db.createObjectStore(STORES.bundle, { keyPath: "key" });
      if (!db.objectStoreNames.contains(STORES.drafts)) db.createObjectStore(STORES.drafts, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORES.photos)) db.createObjectStore(STORES.photos, { keyPath: "photo_id" });
      if (!db.objectStoreNames.contains(STORES.meta)) db.createObjectStore(STORES.meta, { keyPath: "key" });
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

async function tx(storeName, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let out;
    try { out = fn(store); } catch (e) { reject(e); return; }
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const req = (r) => new Promise((resolve, reject) => {
  r.onsuccess = () => resolve(r.result);
  r.onerror = () => reject(r.error);
});

/** Ask the browser not to evict our data when storage gets tight. */
export async function requestPersistence() {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch { /* unsupported */ }
  return false;
}

/* ── bundle ─────────────────────────────────────────────────────── */
export async function putBundle(bundle) {
  return tx(STORES.bundle, "readwrite", (s) => s.put({ key: "latest", ...bundle, cached_at: new Date().toISOString() }));
}
export async function getBundle() {
  try {
    const db = await openDB();
    return await req(db.transaction(STORES.bundle).objectStore(STORES.bundle).get("latest"));
  } catch { return null; }
}

/* ── drafts ─────────────────────────────────────────────────────── */
export async function putDraft(draft) {
  return tx(STORES.drafts, "readwrite", (s) => s.put({ ...draft, updated_at: new Date().toISOString() }));
}
export async function getDraft(id) {
  try {
    const db = await openDB();
    return await req(db.transaction(STORES.drafts).objectStore(STORES.drafts).get(id));
  } catch { return null; }
}
export async function allDrafts() {
  try {
    const db = await openDB();
    return (await req(db.transaction(STORES.drafts).objectStore(STORES.drafts).getAll())) || [];
  } catch { return []; }
}
export async function deleteDraft(id) {
  return tx(STORES.drafts, "readwrite", (s) => s.delete(id));
}

/* ── photos ─────────────────────────────────────────────────────── */
export async function putPhoto(photo) {
  return tx(STORES.photos, "readwrite", (s) => s.put(photo));
}
export async function getPhoto(photoId) {
  try {
    const db = await openDB();
    return await req(db.transaction(STORES.photos).objectStore(STORES.photos).get(photoId));
  } catch { return null; }
}
export async function photosForInspection(inspectionId) {
  const all = await (async () => {
    try {
      const db = await openDB();
      return (await req(db.transaction(STORES.photos).objectStore(STORES.photos).getAll())) || [];
    } catch { return []; }
  })();
  return all.filter((p) => p.inspection_id === inspectionId);
}
export async function deletePhoto(photoId) {
  return tx(STORES.photos, "readwrite", (s) => s.delete(photoId));
}

/* ── meta ───────────────────────────────────────────────────────── */
export async function setMeta(key, value) {
  return tx(STORES.meta, "readwrite", (s) => s.put({ key, value }));
}
export async function getMeta(key) {
  try {
    const db = await openDB();
    const row = await req(db.transaction(STORES.meta).objectStore(STORES.meta).get(key));
    return row?.value;
  } catch { return undefined; }
}
